// Lib
import { useEffect, useState } from 'react';
import { pure } from 'recompose';
import { defer } from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Config
import getClientConfig from '../../utils/getClientConfig';

// Utils
import { enableAmplitudeTracking, initialiseAmplitudeData } from '../../analytics/amplitudeService';
import { addCookieBannerPageOverlay, cookieGroupAllowed } from './oneTrustUtils';
import { getCurrentUserId, getCurrentUserOTToken, isGuestSelector } from '../../user/currentUserSelector';
import useOneTrustLoader from './useOneTrustLoader';
import { hideOneTrustBanner, setOneTrustConsentModel, setOneTrustStatus, showOneTrustBanner } from './oneTrustActions';

// Constants
import { COOKIE_GROUPS, ONE_TRUST_CONSENT_MODEL, ONE_TRUST_STATUS } from './oneTrustConstants';

const mapStateToProps = createStructuredSelector({
    isGuest: isGuestSelector,
    userId: getCurrentUserId,
    userOTToken: getCurrentUserOTToken,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSetOneTrustStatus: (status) => dispatch(setOneTrustStatus(status)),
    dispatchSetOneTrustConsentModel: (consentModel) => dispatch(setOneTrustConsentModel(consentModel)),
    dispatchShowOneTrustBanner: () => dispatch(showOneTrustBanner()),
    dispatchHideOneTrustBanner: () => dispatch(hideOneTrustBanner()),
});

const OneTrustInit = pure((props) => {
    const {
        isGuest,
        userId,
        userOTToken,
        dispatchSetOneTrustStatus,
        dispatchSetOneTrustConsentModel,
        dispatchShowOneTrustBanner,
        dispatchHideOneTrustBanner,
    } = props;

    const config = getClientConfig();

    const [oneTrustState] = useOneTrustLoader({ isGuest, userId, userOTToken });
    const [performanceCookiesAllowed, setPerformanceCookiesAllowed] = useState(() =>
        cookieGroupAllowed(COOKIE_GROUPS.PERFORMANCE_COOKIES),
    );

    // On an initial load of the component, initialise OneTrust display and some function hooks
    useEffect(() => {
        if (oneTrustState === ONE_TRUST_STATUS.INITIALISED) {
            const showCookieBanner = !window.OneTrust.IsAlertBoxClosed();
            if (showCookieBanner) {
                addCookieBannerPageOverlay();

                // For now, use consent model to determine whether consent banner needs to be displayed.
                // This might change in the future, e.g. CCPA banner would need to be shown even when its consent is opt-out.
                const consentModel = window.OneTrust.GetDomainData()?.ConsentModel?.Name;

                dispatchShowOneTrustBanner();
                dispatchSetOneTrustConsentModel(consentModel);

                const requiresConsent = consentModel === ONE_TRUST_CONSENT_MODEL.OPT_IN;
                if (requiresConsent) {
                    document.body.classList.add('ot-requires-consent');

                    // Focus on banner once the call stack is cleared.
                    // Deferring is required to make sure that the banner is focused, as sometimes focus on
                    // form input (in login and register pages) is called first
                    defer(() => document.getElementById('onetrust-banner-sdk')?.focus());
                } else {
                    document.body.classList.add('ot-optional-consent');
                }
            }

            setPerformanceCookiesAllowed(cookieGroupAllowed(COOKIE_GROUPS.PERFORMANCE_COOKIES));

            // eslint-disable-next-line new-cap
            window.OneTrust.OnConsentChanged(() => {
                setPerformanceCookiesAllowed(cookieGroupAllowed(COOKIE_GROUPS.PERFORMANCE_COOKIES));

                if (window.OneTrust.IsAlertBoxClosed()) {
                    dispatchHideOneTrustBanner();

                    // Remove any OneTrust classes from the document body
                    document.body.classList.remove('ot-requires-consent');
                    document.body.classList.remove('ot-optional-consent');
                }
            });
        }

        dispatchSetOneTrustStatus(oneTrustState);
    }, [oneTrustState]);

    // When performanceCookiesAllowed value change, determine whether or not to enable/disable analytics
    useEffect(() => {
        if (performanceCookiesAllowed === null) return;

        enableAmplitudeTracking(performanceCookiesAllowed);

        if (performanceCookiesAllowed) {
            initialiseAmplitudeData(config);
        }
    }, [performanceCookiesAllowed]);

    return null;
});

export default connect(mapStateToProps, mapDispatchToProps)(OneTrustInit);
